import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DateTimeInput } from "@/components/DateTimeInput";
import { api, errMsg } from "@/lib/api";
import { parseWIB, toWIBInput } from "@/lib/datetime";
import { BOOKING_FORM } from "@/constants/testIds";

const EMPTY = { name: "", booking_code: "", payment: "", guest: "", paid: false, guest_done: false };

const DTField = ({ label, hint, value, onChange, testid }) => {
  const invalid = value.length === 16 && !parseWIB(value);
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <DateTimeInput value={value} onChange={onChange} invalid={invalid} data-testid={testid} />
      <p className={`text-xs ${invalid ? "text-red-600" : "text-slate-500"}`}>{invalid ? "Tanggal/jam tidak valid" : hint}</p>
    </div>
  );
};

const Check = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
    <Checkbox checked={checked} onCheckedChange={(v) => onChange(!!v)} data-testid={testid} /> {label}
  </label>
);

export const BookingDialog = ({ open, onOpenChange, booking, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (open) {
      setF(booking ? { name: booking.name, booking_code: booking.booking_code, payment: toWIBInput(booking.payment_deadline),
        guest: toWIBInput(booking.guest_deadline), paid: booking.paid, guest_done: booking.guest_done } : EMPTY);
    }
  }, [open, booking]);

  const submit = async (e) => {
    e.preventDefault();
    const payment = parseWIB(f.payment);
    const guest = parseWIB(f.guest);
    if (!payment || !guest) return toast.error("Lengkapi tanggal dan jam dengan format DD MM YYYY JJ:MM");
    setSaving(true);
    const body = { name: f.name, booking_code: f.booking_code, payment_deadline: payment, guest_deadline: guest, paid: f.paid, guest_done: f.guest_done };
    try {
      if (booking) await api.put(`/bookings/${booking.id}`, body);
      else await api.post("/bookings", body);
      toast.success(booking ? "Booking diperbarui" : "Booking ditambahkan");
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid={BOOKING_FORM.dialog}>
        <DialogHeader><DialogTitle className="font-heading tracking-tight">{booking ? "Revisi Booking" : "Tambah Booking"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label>Nama</Label><Input value={f.name} onChange={(e) => set("name")(e.target.value)} required data-testid={BOOKING_FORM.name} /></div>
            <div className="space-y-2"><Label>Kode Book</Label><Input value={f.booking_code} onChange={(e) => set("booking_code")(e.target.value)} required className="uppercase" data-testid={BOOKING_FORM.code} /></div>
          </div>
          <DTField label="Timelimit Pelunasan" hint="Pengingat mulai H-2 (WIB)" value={f.payment} onChange={set("payment")} testid={BOOKING_FORM.payment} />
          <DTField label="Timelimit Guest Data" hint="Pengingat mulai H-3 (WIB)" value={f.guest} onChange={set("guest")} testid={BOOKING_FORM.guest} />
          <div className="flex flex-wrap gap-6 rounded-md bg-slate-50 p-3">
            <Check checked={f.paid} onChange={set("paid")} label="Sudah Lunas" testid={BOOKING_FORM.paid} />
            <Check checked={f.guest_done} onChange={set("guest_done")} label="Guest Data Lengkap" testid={BOOKING_FORM.guestDone} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Batal</Button>
            <Button type="submit" disabled={saving} className="rounded-full bg-brand hover:bg-brand-dark text-white" data-testid={BOOKING_FORM.submit}>{saving ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
